import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TablePagination,
  Tooltip,
} from '@mui/material'
import React from 'react'
import { Department } from 'enums/department'

import { useDispatch } from 'react-redux'
import API from '@aws-amplify/api'
import MuiAlert from '@mui/material/Alert'
import ConfirmDialog from 'components/confirmDialog'
import { getFriendlyErrorMessage } from 'utils/errorHandling'
import { ISalesOrder, ISalesOrderProduct } from 'interfaces/ISalesOrder'
import { IStockCard } from 'interfaces/IStockCard'
import { formatTimestamp } from 'shared/dateFormatter'
import { STOCK_CARD_TYPE_HALF_COMPLETE } from 'enums/stockCard'
import {
  getLabelingStjSummaryRows,
  getStjSummaryRowKey,
  getStjSummaryTotalQty,
  StjSummaryRow,
} from './labelingStjSummary'

const lastMonth = new Date()
lastMonth.setMonth(lastMonth.getMonth() - 1)

export default function LabelingInventory(props: { department: Department }) {
  const { department } = props
  const [isLoading, setIsLoading] = React.useState<any>(false)

  const [alertMessage, setAlertMessage] = React.useState('')
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [salesOrders, setSalesOrders] = React.useState<ISalesOrder[]>([])
  const [selectedSalesOrderProduct, setSelectedSalesOrderProduct] =
    React.useState<ISalesOrderProduct | null>()
  const [isConfirmingLabelPrinted, setIsConfirmingLabelPrinted] =
    React.useState(false)
  const [isConfirmingLabelReceived, setIsConfirmingLabelReceived] =
    React.useState(false)
  const [isConfirmingLabelingComplete, setIsConfirmingLabelingComplete] =
    React.useState(false)
  const [isConfirmingLabelingReturned, setIsConfirmingLabelingReturned] =
    React.useState(false)

  const [halfCompleteContext, setHalfCompleteContext] = React.useState<{
    salesOrder: ISalesOrder | null
    salesOrderProduct: ISalesOrderProduct | null
    stockCards: IStockCard[]
  }>({
    salesOrder: null,
    salesOrderProduct: null,
    stockCards: [],
  })

  // Restored state variables for other dialogs
  const [labelAmount, setLabelAmount] = React.useState<number | string | null>(
    null
  )
  const [labelReceivedAmount, setLabelReceivedAmount] = React.useState<
    number | string | null
  >(null)
  const [labelConfirmedReturnedAmount, setLabelConfirmedReturnedAmount] =
    React.useState<number | string | null>(null)
  const [labelRejectedReceivedAmount, setLabelRejectedReceivedAmount] =
    React.useState<number | string | null>(null)

  const [selectedProductIds, setSelectedProductIds] = React.useState<number[]>(
    []
  )
  const [labelingData, setLabelingData] = React.useState<
    Record<
      number,
      {
        labelUsedAmount: string
        labelRejectedAmount: string
        labelReturnedAmount: string
        qtyKeJadi: string
        batchData: Record<string, string> // keyed by arrival ID or index
      }
    >
  >({})
  const [labelingContextSalesOrder, setLabelingContextSalesOrder] =
    React.useState<ISalesOrder | null>(null)

  const [
    isConfirmingHalfCompleteReceived,
    setIsConfirmingHalfCompleteReceived,
  ] = React.useState(false)

  // REMOVED: finishedGoodsQtyRows state (no longer used)

  const [labelingValidationError, setLabelingValidationError] = React.useState<
    string | null
  >(null)
  const [isLabelingFormValid, setIsLabelingFormValid] = React.useState(true)
  // Track received amounts for each Sales Order Product (keyed by salesOrderProductId)
  const [soProductReceivedAmounts, setSoProductReceivedAmounts] =
    React.useState<Record<number, string>>({})
  const [showOnlySisaLabelReceived, setShowOnlySisaLabelReceived] =
    React.useState(false)

  const flattenedStjItems = React.useMemo(() => {
    const items: Array<{
      qty: number
      packagingId: string
      premixId: string
      batchNumber: string
      fillingAmount: number | null
      fillingUnit: string
    }> = []

    halfCompleteContext.stockCards.forEach((sc) => {
      if (sc.stockCardProducts && sc.stockCardProducts.length > 0) {
        sc.stockCardProducts.forEach((scp) => {
          items.push({
            qty: Number(scp.quantity || 0),
            packagingId: scp.packagingId || sc.packagingId || '-',
            premixId: scp.premixId || sc.premixId || '-',
            batchNumber: scp.batchNumber || sc.batchNumber || '-',
            fillingAmount:
              scp.fillingAmount != null
                ? Number(scp.fillingAmount)
                : sc.fillingAmount,
            fillingUnit: scp.fillingUnit || sc.fillingUnit || '',
          })
        })
      } else {
        items.push({
          qty: Number(sc.quantity || 0),
          packagingId: sc.packagingId || '-',
          premixId: sc.premixId || '-',
          batchNumber: sc.batchNumber || '-',
          fillingAmount: sc.fillingAmount,
          fillingUnit: sc.fillingUnit || '',
        })
      }
    })

    // Group by the unique attributes combo
    const grouped = new Map<string, any>()
    items.forEach((item) => {
      // Normalize values for grouping key to prevent duplicates from minor string mismatches
      const pId = String(item.packagingId || '-').trim()
      const bId = String(item.premixId || '-').trim()
      const batchNum = String(item.batchNumber || '-').trim()
      const amount = Number(item.fillingAmount || 0)
      const unit = String(item.fillingUnit || '')
        .trim()
        .toUpperCase()

      const key = `${pId}|${bId}|${batchNum}|${amount}|${unit}`
      if (grouped.has(key)) {
        grouped.get(key).qty += item.qty
      } else {
        grouped.set(key, { ...item })
      }
    })

    return Array.from(grouped.values())
  }, [halfCompleteContext.stockCards])

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const [totalCount, setTotalCount] = React.useState(0)

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    getSalesOrders({ pageParam: newPage, pageSizeParam: rowsPerPage })
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    getSalesOrders({ pageParam: 0, pageSizeParam: newRowsPerPage })
  }

  const handleLabelingItemChange = (
    salesOrderProductId: number,
    field:
      | 'labelUsedAmount'
      | 'labelRejectedAmount'
      | 'labelReturnedAmount'
      | 'qtyKeJadi',
    value: string
  ) => {
    setLabelingData((prev) => {
      const existing = prev[salesOrderProductId] || {
        labelUsedAmount: '',
        labelRejectedAmount: '',
        labelReturnedAmount: '',
        qtyKeJadi: '',
        batchData: {},
      }
      return {
        ...prev,
        [salesOrderProductId]: {
          ...existing,
          [field]: value,
        },
      }
    })
  }

  const handleBatchQtyChange = (
    salesOrderProductId: number,
    batchId: string,
    value: string
  ) => {
    setLabelingData((prev) => {
      const existing = prev[salesOrderProductId] || {
        labelUsedAmount: '',
        labelRejectedAmount: '',
        labelReturnedAmount: '',
        qtyKeJadi: '',
        batchData: {},
      }
      const newBatchData = {
        ...existing.batchData,
        [batchId]: value,
      }
      // Sum up all batch quantities to update the total qtyKeJadi
      const total = Object.values(newBatchData).reduce(
        (sum, val) => sum + toNumberOrZero(val),
        0
      )

      return {
        ...prev,
        [salesOrderProductId]: {
          ...existing,
          batchData: newBatchData,
          qtyKeJadi: String(total),
        },
      }
    })
  }

  const formatNumericDisplay = (value?: number | string | null): string => {
    if (value == null || value === '') {
      return '-'
    }

    const num = Number(value)
    if (isNaN(num)) return String(value)

    return num.toFixed(6)
  }

  const toNumberOrZero = (value: string | number | null | undefined) => {
    if (value == null || value === '') {
      return 0
    }
    const numericValue = Number(value)
    if (Number.isNaN(numericValue)) {
      return 0
    }
    return numericValue
  }

  const validateLabelingForm = React.useCallback(() => {
    if (!labelingContextSalesOrder) {
      // Allow valid if simply closing or not in context
      return { isValid: true, message: null }
    }

    const productsToValidate =
      labelingContextSalesOrder.salesOrderProducts.filter(
        (p) =>
          labelingData[p.salesOrderProductId] !== undefined &&
          selectedProductIds.includes(p.salesOrderProductId) &&
          p.labelingCompletedAt == null &&
          p.labelReceivedAt != null
      )

    if (productsToValidate.length === 0) {
      return {
        isValid: false,
        message: 'Tidak ada produk yang siap untuk diselesaikan labelingnya.',
      }
    }

    for (const product of productsToValidate) {
      const data = labelingData[product.salesOrderProductId]
      if (!data) continue

      const labelReceivedTarget = Number(
        product.labelReceivedAmount ?? product.productQty ?? 0
      )

      const used = toNumberOrZero(data.labelUsedAmount)
      const rejected = toNumberOrZero(data.labelRejectedAmount)
      const returned = toNumberOrZero(data.labelReturnedAmount)
      const distributionSum = used + rejected + returned

      if (distributionSum !== labelReceivedTarget) {
        return {
          isValid: false,
          message: `Produk ${
            product.productName
          }: Total Qty LABEL Ditempel + Qty LABEL Direject + Qty LABEL Dikembalikan (${formatNumericDisplay(
            distributionSum
          )}) harus sama dengan Qty LABEL Diterima Produksi (${formatNumericDisplay(
            labelReceivedTarget
          )}).`,
        }
      }

      const qtyKeJadi = toNumberOrZero(data.qtyKeJadi)
      const qtyDikeluarkan = labelingContextSalesOrder
        ? getStjSummaryTotalQty(
            getLabelingStjSummaryRows(
              labelingContextSalesOrder,
              product,
              department
            )
          )
        : 0

      if (Math.abs(qtyKeJadi - qtyDikeluarkan) > 0.000001 && qtyKeJadi !== 0) {
        // Optional: warn if not matching, but don't block if they intentionally partial
      }

      if (qtyKeJadi > qtyDikeluarkan + 0.000001) {
        return {
          isValid: false,
          message: `Produk ${
            product.productName
          }: Total Qty BARANG ke JADI (${formatNumericDisplay(
            qtyKeJadi
          )}) tidak boleh lebih besar dari total Qty Dikeluarkan (${formatNumericDisplay(
            qtyDikeluarkan
          )}).`,
        }
      }

      if (labelingContextSalesOrder) {
        const stjRows = getLabelingStjSummaryRows(
          labelingContextSalesOrder,
          product,
          department
        )
        for (let index = 0; index < stjRows.length; index++) {
          const row = stjRows[index]
          const batchId = getStjSummaryRowKey(row, index)
          const batchQty = toNumberOrZero(data.batchData[batchId])
          const arrivalQty = Number(row.qty || 0)
          if (batchQty > arrivalQty + 0.000001) {
            return {
              isValid: false,
              message: `Produk ${product.productName}, Batch ${
                row.batchNumber
              }: Qty BARANG ke JADI (${formatNumericDisplay(
                batchQty
              )}) tidak boleh lebih besar dari Qty Dikeluarkan (${formatNumericDisplay(
                arrivalQty
              )}).`,
            }
          }
        }
      }
    }

    return { isValid: true, message: null }
  }, [department, labelingContextSalesOrder, labelingData, selectedProductIds])

  const getSalesOrders = React.useCallback(
    async (options?: {
      showLoading?: boolean
      pageParam?: number
      pageSizeParam?: number
    }): Promise<void> => {
      const showLoading = options?.showLoading ?? true
      const pageParam = options?.pageParam ?? page
      const pageSizeParam = options?.pageSizeParam ?? rowsPerPage

      if (showLoading) {
        setIsLoading(true)
      }
      setSalesOrders([])

      const queryStringParameters: any = {
        pageSize: pageSizeParam,
        page: pageParam + 1,
        isLabelRequested: true,
      }

      if (department) {
        queryStringParameters.department = department
      }

      const param = {
        queryStringParameters,
      }

      try {
        const response = (await API.get(
          'products',
          `/salesOrders/getSalesOrder`,
          param
        )) as { salesOrders: ISalesOrder[]; count: number }
        setSalesOrders(response.salesOrders)
        setTotalCount(response.count)
      } catch (error) {
        setAlertMessage('Gagal mendapatkan daftar labeling.')
        setIsFailureSnackbarOpen(true)
      } finally {
        if (showLoading) {
          setIsLoading(false)
        }
      }
    },
    [page, rowsPerPage, department]
  )

  const markLabelPrinted = async () => {
    if (!selectedSalesOrderProduct) {
      return
    }
    setIsLoading(true)

    try {
      await API.patch(
        'products',
        `/salesOrders/markLabelPrinted/${selectedSalesOrderProduct.salesOrderId}`,
        {
          body: {
            salesOrderProductId: selectedSalesOrderProduct.salesOrderProductId,
            labelAmount: Number(labelAmount),
          },
        }
      )
      const nowIso = new Date().toISOString()
      setSalesOrders((prev) =>
        prev.map((salesOrder) => ({
          ...salesOrder,
          salesOrderProducts: salesOrder.salesOrderProducts.map((product) =>
            product.salesOrderProductId ===
            selectedSalesOrderProduct.salesOrderProductId
              ? {
                  ...product,
                  labelAmount: Number(labelAmount),
                  labelPrintedAt: product.labelPrintedAt ?? nowIso,
                }
              : product
          ),
        }))
      )
      setAlertMessage('Berhasil menandai labeling dicetak.')
      setIsSuccessSnackbarOpen(true)
      await getSalesOrders()
      setIsConfirmingLabelPrinted(false)
      setLabelAmount(null)
    } catch (error) {
      setAlertMessage(
        getFriendlyErrorMessage(error, 'Gagal menandai labeling dicetak.')
      )
      setIsFailureSnackbarOpen(true)
      setIsLoading(false)
    }
  }
  const markLabelingCompleted = async () => {
    if (!labelingContextSalesOrder) {
      return
    }

    const validationResult = validateLabelingForm()
    if (!validationResult.isValid) {
      setLabelingValidationError(validationResult.message)
      setIsLabelingFormValid(false)
      return
    }

    setIsLoading(true)
    let hasError = false

    const productsToProcess =
      labelingContextSalesOrder.salesOrderProducts.filter(
        (p) =>
          labelingData[p.salesOrderProductId] !== undefined &&
          selectedProductIds.includes(p.salesOrderProductId) &&
          p.labelingCompletedAt == null &&
          p.labelReceivedAt != null
      )

    for (const product of productsToProcess) {
      const data = labelingData[product.salesOrderProductId]
      try {
        const stjRows = getStjSummaryRows(labelingContextSalesOrder, product)
        const batchDataArr = stjRows.map((row, index) => {
          const batchId = getStjSummaryRowKey(row, index)
          const qty = toNumberOrZero(data.batchData[batchId])
          return {
            halfCompleteBatchNumber: row.batchNumber || '',
            qtyKeJadi: qty,
            halfCompletePackagingId: row.packagingId,
            halfCompletePremixId: row.premixId,
            halfCompleteFillingAmount:
              row.fillingAmount != null ? Number(row.fillingAmount) : null,
            halfCompleteFillingUnit: row.fillingUnit,
          }
        })

        await API.patch(
          'products',
          `/salesOrders/markLabelingCompleted/${product.salesOrderId}`,
          {
            body: {
              salesOrderProductId: product.salesOrderProductId,
              labelUsedAmount: toNumberOrZero(data.labelUsedAmount),
              labelReturnedAmount: toNumberOrZero(data.labelReturnedAmount),
              labelRejectedAmount: toNumberOrZero(data.labelRejectedAmount),
              qtyKeJadi: toNumberOrZero(data.qtyKeJadi),
              batchData: batchDataArr,
            },
          }
        )
      } catch (error) {
        console.error(error)
        hasError = true
      }
    }

    if (hasError) {
      setAlertMessage(
        'Beberapa item gagal ditandai selesai (mungkin sudah diproses). Silakan segarkan halaman.'
      )
      setIsFailureSnackbarOpen(true)
    } else {
      setAlertMessage('Berhasil menandai labeling selesai untuk semua item.')
      setIsSuccessSnackbarOpen(true)
      await getSalesOrders()
      setIsConfirmingLabelingComplete(false)
      setLabelingData({})
      setSelectedProductIds([])
      setLabelingContextSalesOrder(null)
    }

    setIsLoading(false)
  }

  const markLabellingReceived = async () => {
    if (!selectedSalesOrderProduct) {
      return
    }
    setIsLoading(true)
    try {
      await API.patch(
        'products',
        `/salesOrders/markLabelingReceived/${selectedSalesOrderProduct.salesOrderId}`,
        {
          body: {
            salesOrderProductId: selectedSalesOrderProduct.salesOrderProductId,
            labelReceivedAmount: Number(labelReceivedAmount),
          },
        }
      )
      setAlertMessage('Berhasil menandai labeling selesai.')
      setIsSuccessSnackbarOpen(true)
      setLabelReceivedAmount(null)
      await getSalesOrders()
    } catch (error) {
      setAlertMessage(
        getFriendlyErrorMessage(error, 'Gagal menandai labeling selesai.')
      )
      setIsFailureSnackbarOpen(true)
      setIsLoading(false)
    }
  }

  const markLabellingReturned = async () => {
    if (!selectedSalesOrderProduct) {
      return
    }
    setIsLoading(true)
    try {
      await API.patch(
        'products',
        `/salesOrders/markLabelingReturned/${selectedSalesOrderProduct.salesOrderId}`,
        {
          body: {
            salesOrderProductId: selectedSalesOrderProduct.salesOrderProductId,
            labelAmount: labelConfirmedReturnedAmount,
            labelRejectedReceivedAmount,
          },
        }
      )
      setAlertMessage('Berhasil menandai labeling selesai diterima.')
      setIsSuccessSnackbarOpen(true)
      await getSalesOrders()
      setLabelConfirmedReturnedAmount(null)
      setLabelRejectedReceivedAmount(null)
      setIsConfirmingLabelingReturned(false)
    } catch (error) {
      setAlertMessage(
        getFriendlyErrorMessage(
          error,
          'Gagal menandai labeling selesai diterima.'
        )
      )
      setIsFailureSnackbarOpen(true)
      setIsLoading(false)
    }
  }

  const getHalfCompleteQuantity = (stockCard: IStockCard | null) => {
    if (!stockCard) {
      return null
    }
    const qty = stockCard.confirmedQuantity ?? stockCard.quantity
    if (qty == null) {
      return null
    }
    return Math.abs(Number(qty))
  }

  const formatFillingAmountDisplay = (
    amount?: number | null,
    unit?: string | null
  ) => {
    if (amount == null || Number.isNaN(Number(amount))) {
      return '-'
    }
    const suffix = unit ? ` ${unit}` : ''
    return `${formatNumericDisplay(amount)}${suffix}`.trim()
  }

  const formatStjSummaryNumber = (value?: number | string | null): string => {
    if (value == null || value === '') {
      return '-'
    }

    const num = Number(value)
    if (Number.isNaN(num)) return String(value)

    return num.toFixed(6).replace(/\.?0+$/, '')
  }

  const getStjSummaryRows = (
    salesOrder: ISalesOrder,
    salesOrderProduct: ISalesOrderProduct
  ): StjSummaryRow[] =>
    getLabelingStjSummaryRows(salesOrder, salesOrderProduct, department)

  const renderStjSummaryTable = (
    salesOrder: ISalesOrder,
    salesOrderProduct: ISalesOrderProduct
  ) => {
    const rows = getStjSummaryRows(salesOrder, salesOrderProduct)

    if (rows.length === 0) {
      return null
    }

    return (
      <Box mt={2}>
        <Typography variant="h6">Summary Barang STJ diminta</Typography>
        <TableContainer style={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Qty Dikeluarkan</TableCell>
                <TableCell>Kode Kemasan</TableCell>
                <TableCell>Kode Ruahan</TableCell>
                <TableCell>No Bets</TableCell>
                <TableCell>Filling Amount</TableCell>
                <TableCell>Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={`stj-summary-${row.packagingId}-${row.premixId}-${row.batchNumber}-${index}`}
                >
                  <TableCell>{formatStjSummaryNumber(row.qty)}</TableCell>
                  <TableCell>{row.packagingId || '-'}</TableCell>
                  <TableCell>{row.premixId || '-'}</TableCell>
                  <TableCell>{row.batchNumber || '-'}</TableCell>
                  <TableCell>
                    {row.fillingAmount != null
                      ? `${formatStjSummaryNumber(row.fillingAmount)} ${
                          row.fillingUnit || ''
                        }`.trim()
                      : '-'}
                  </TableCell>
                  <TableCell>{formatStjSummaryNumber(row.stock)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  const resetHalfCompleteContext = () => {
    setHalfCompleteContext({
      salesOrder: null,
      salesOrderProduct: null,
      stockCards: [],
    })
    setSoProductReceivedAmounts({})
  }

  const markHalfCompleteReceived = async () => {
    if (
      !halfCompleteContext.salesOrder ||
      halfCompleteContext.stockCards.length === 0 ||
      Object.keys(soProductReceivedAmounts).length === 0
    ) {
      return
    }
    setIsLoading(true)
    const { salesOrder, stockCards } = halfCompleteContext

    // 1. Initialize balances for available stock cards
    const cardBalances = new Map<number, number>()
    stockCards.forEach((sc) => {
      cardBalances.set(
        sc.stockCardId as number,
        Number(getHalfCompleteQuantity(sc) || 0)
      )
    })

    // 2. Distribute product requirements across stock cards
    // Resulting structure: stockCardId -> { products: SubProductMapping[] }
    const distributionResult = new Map<number, any[]>()

    console.log('--- START STJ DISTRIBUTION DEBUG ---')
    console.log(
      'Target SO Products:',
      salesOrder.salesOrderProducts.map((p) => ({
        id: p.salesOrderProductId,
        name: p.productName,
        qty: p.productQty,
      }))
    )
    console.log('Received Amounts from UI:', soProductReceivedAmounts)

    // Iterate through user inputs for each SO product
    Object.entries(soProductReceivedAmounts).forEach(([id, amountStr]) => {
      const productId = Number(id)
      const amountRequested = Number(amountStr)
      if (amountRequested <= 0) return

      const targetProduct = salesOrder.salesOrderProducts.find(
        (p) => p.salesOrderProductId === productId
      )
      if (!targetProduct) {
        console.error(
          `Product with ID ${productId} not found in Sales Order ${salesOrder.salesOrderId}`
        )
        return
      }

      console.log(
        `\nProcessing requirement for: ${targetProduct.productName} (ID: ${productId})`
      )
      console.log(`Requested: ${amountRequested}`)

      let remainingToFill = amountRequested

      // Find stock cards that match this product requirements using a multi-tiered fallback
      const matchingCards = stockCards.filter((sc) => {
        const scps = sc.stockCardProducts || []

        const checkTierMatch = (p: any, isSubItem: boolean) => {
          const pId = String(p.productId || '').trim()
          const targetPId = String(targetProduct.productId || '').trim()
          const packId = String(p.packagingId || '').trim()
          const targetPackId = String(targetProduct.packagingId || '').trim()
          const premixId = String(p.premixId || '').trim()
          const targetPremixId = String(targetProduct.premixId || '').trim()
          const vol = Number(p.fillingAmount || 0)
          const targetVol = Number(targetProduct.volume || 0)

          console.log(
            `Checking card ${sc.stockCardId} ${
              isSubItem ? '(Sub-item)' : '(Root)'
            }:`
          )
          console.log(
            `  IDs: ${pId} vs ${targetPId} | Packaging: ${packId} vs ${targetPackId} | Bulk: ${premixId} vs ${targetPremixId} | Vol: ${vol} vs ${targetVol}`
          )

          // Tier 1: Strict Product ID Match
          if (pId === targetPId && pId !== '') {
            console.log('  -> MATCH: Tier 1 (Product ID)')
            return true
          }

          // Tier 2: Physical Profile Match (Packaging + Premix)
          // Removed volumeMatch check as per request to focus on attributes
          if (packId === targetPackId && premixId === targetPremixId) {
            console.log('  -> MATCH: Tier 2 (Physical Profile)')
            return true
          }

          // Tier 3: Loose Profile Match (Premix + (Generic Packaging or ID match))
          // Removed volumeMatch check. If bulk matches and packaging is blank, we take it.
          if (premixId === targetPremixId && (!packId || packId === '')) {
            console.log('  -> MATCH: Tier 3 (Loose Profile)')
            return true
          }

          return false
        }

        // Check if root or any sub-product matches any tier
        const isRootMatch = checkTierMatch(sc, false)
        const isSubMatch = scps.some((scp) => checkTierMatch(scp, true))

        if (isRootMatch || isSubMatch) return true

        // Tier 4: Batch Number Fallback
        if (targetProduct.batchNumber) {
          const productBatches = targetProduct.batchNumber
            .split(',')
            .map((b) => b.trim())

          const matchesRootBatch =
            sc.batchNumber && productBatches.includes(sc.batchNumber.trim())
          const matchesSubBatch = scps.some(
            (scp) =>
              scp.batchNumber && productBatches.includes(scp.batchNumber.trim())
          )

          if (matchesRootBatch || matchesSubBatch) {
            // If batch matches, still verify basic compatibility (premix or packaging)
            const rootCompatible =
              sc.premixId === targetProduct.premixId ||
              sc.packagingId === targetProduct.packagingId
            const subCompatible = scps.some(
              (scp) =>
                scp.premixId === targetProduct.premixId ||
                scp.packagingId === targetProduct.packagingId
            )
            if (rootCompatible || subCompatible) return true
          }
        }

        return false
      })

      console.log(
        `Found ${matchingCards.length} matching cards for ${targetProduct.productName}`
      )

      for (const sc of matchingCards) {
        if (remainingToFill <= 0) break

        const scId = sc.stockCardId as number
        const balance = cardBalances.get(scId) || 0
        if (balance <= 0) {
          console.log(`Card ${scId} has no remaining balance, skipping.`)
          continue
        }

        const amountToTake = Math.min(remainingToFill, balance)
        console.log(
          `Taking ${amountToTake} from Card ${scId} (Card Balance: ${balance})`
        )

        // Record contribution
        const currentProducts = distributionResult.get(scId) || []
        currentProducts.push({
          productId: targetProduct.productId,
          confirmedQuantity: amountToTake,
          packagingId: targetProduct.packagingId,
          premixId: targetProduct.premixId,
          batchNumber: targetProduct.batchNumber || sc.batchNumber || '',
          fillingAmount: targetProduct.fillingAmount || sc.fillingAmount,
          fillingUnit: targetProduct.fillingUnit || sc.fillingUnit,
          labelId: targetProduct.labelId,
          salesOrderProductId: targetProduct.salesOrderProductId,
        })
        distributionResult.set(scId, currentProducts)

        // Update balances
        cardBalances.set(scId, balance - amountToTake)
        remainingToFill -= amountToTake
      }

      if (remainingToFill > 0) {
        console.warn(
          `WARNING: Failed to fully satisfy requirement for ${targetProduct.productName}. Still need ${remainingToFill}`
        )
      }
    })

    // 3. Form the final payload
    const stockCardData = Array.from(distributionResult.entries()).map(
      ([scId, products]) => {
        const stockCard = stockCards.find((sc) => sc.stockCardId === scId)!
        const totalAmount = products.reduce(
          (sum, p) => sum + p.confirmedQuantity,
          0
        )

        return {
          stockCardId: scId,
          receivedAmount: totalAmount,
          productId: stockCard.productId,
          products: products,
          packagingId: stockCard.packagingId,
          premixId: stockCard.premixId,
          batchNumber: stockCard.batchNumber || '',
          fillingAmount: stockCard.fillingAmount,
          fillingUnit: stockCard.fillingUnit,
        }
      }
    )

    console.log('Final Stock Card Data to send to backend:', stockCardData)
    console.log('--- END STJ DISTRIBUTION DEBUG ---')

    const totalReceivedQty = stockCardData.reduce(
      (sum, card) => sum + card.receivedAmount,
      0
    )

    if (stockCardData.length === 0) {
      setAlertMessage('Silakan masukkan jumlah yang diterima.')
      setIsFailureSnackbarOpen(true)
      setIsLoading(false)
      return
    }

    try {
      await API.patch(
        'products',
        `/salesOrders/markHalfCompleteReceived/${salesOrder.salesOrderId}`,
        {
          body: {
            salesOrderProductId:
              halfCompleteContext.salesOrderProduct?.salesOrderProductId,
            halfCompleteQty: totalReceivedQty,
            stockCardData,
          },
        }
      )
      setAlertMessage('Berhasil menandai STJ diterima.')
      setIsSuccessSnackbarOpen(true)
      await getSalesOrders()
      setIsConfirmingHalfCompleteReceived(false)
      resetHalfCompleteContext()
    } catch (error) {
      setAlertMessage('Gagal menandai STJ diterima.')
      setIsFailureSnackbarOpen(true)
      setIsLoading(false)
    }
  }

  const renderLabelReceivedSection = (
    salesOrderProduct: ISalesOrderProduct
  ) => {
    if (!salesOrderProduct.labelReceivedAt) {
      const reasons: string[] = []
      if (salesOrderProduct.labelPrintedAt == null) {
        reasons.push('Label belum dicetak')
      }
      const tooltipTitle =
        reasons.length > 0
          ? `Tidak bisa tandai label diterima: ${reasons.join('; ')}`
          : ''
      return (
        <>
          <Tooltip title={tooltipTitle} arrow>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedSalesOrderProduct(salesOrderProduct)
                  setIsConfirmingLabelReceived(true)
                }}
                disabled={reasons.length > 0 || isLoading}
              >
                Tandai Label Diterima
              </Button>
            </span>
          </Tooltip>
          {reasons.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="error" display="block">
                Tidak bisa: {reasons.join('; ')}
              </Typography>
            </Box>
          )}
        </>
      )
    }

    return (
      <Box>
        <Typography variant="body2">
          <Box component="span" fontWeight="bold" display="inline">
            Tanggal Label Diterima:{' '}
          </Box>
          {formatTimestamp('id-ID', salesOrderProduct.labelReceivedAt)}
        </Typography>
        <Typography variant="body2">
          <Box component="span" fontWeight="bold" display="inline">
            Qty LABEL Diterima Produksi:{' '}
          </Box>
          {salesOrderProduct.labelReceivedAmount}
        </Typography>
      </Box>
    )
  }

  React.useEffect(() => {
    getSalesOrders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dispatch = useDispatch()
  React.useEffect(() => {
    const title = department
      ? `Daftar Labeling (${department.toUpperCase()})`
      : 'Daftar Labeling'
    dispatch({ type: 'SET_PAGE_TITLE', payload: title })
  }, [dispatch, department])

  React.useEffect(() => {
    getSalesOrders()
  }, [department, getSalesOrders]) // Re-fetch when department changes

  React.useEffect(() => {
    if (!isConfirmingLabelingComplete) {
      setLabelingValidationError(null)
      setIsLabelingFormValid(true)
      setLabelingData({})
      setSelectedProductIds([])
      setLabelingContextSalesOrder(null)
      return
    }
  }, [isConfirmingLabelingComplete])

  React.useEffect(() => {
    if (!isConfirmingLabelingComplete) {
      return
    }

    // Delay validation slightly to avoid initial render issues or check if context is ready
    if (labelingContextSalesOrder && Object.keys(labelingData).length > 0) {
      const validationResult = validateLabelingForm()
      setLabelingValidationError(validationResult.message)
      setIsLabelingFormValid(validationResult.isValid)
    }
  }, [
    isConfirmingLabelingComplete,
    labelingData,
    validateLabelingForm,
    labelingContextSalesOrder,
  ])

  const filteredSalesOrders = React.useMemo(() => {
    return salesOrders
      .map((so) => {
        const filteredProducts = so.salesOrderProducts.filter((sop) => {
          // Daftar Labeling should show all items with label requested,
          // even if printing/receiving/labeling steps haven't been completed yet.
          const hasLabelRequested = sop.labelRequestedAt != null
          if (!hasLabelRequested) {
            return false
          }

          const isSisaLabelReceived = sop.labelReturnedAt != null
          return showOnlySisaLabelReceived
            ? isSisaLabelReceived
            : !isSisaLabelReceived
        })
        return { ...so, salesOrderProducts: filteredProducts }
      })
      .filter((so) => so.salesOrderProducts.length > 0)
  }, [salesOrders, showOnlySisaLabelReceived])

  return (
    <Box>
      <Box mb={2} display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={showOnlySisaLabelReceived}
              onChange={(e) => setShowOnlySisaLabelReceived(e.target.checked)}
              color="primary"
            />
          }
          label="Hanya sisa label sudah diterima"
        />
      </Box>
      <Paper>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer style={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nomor SO</TableCell>
                  <TableCell>Tanggal SO Diterima</TableCell>
                  <TableCell>Merek</TableCell>
                  <TableCell>Nama</TableCell>
                  <TableCell>Kemasan</TableCell>
                  <TableCell>BX</TableCell>
                  <TableCell>HL</TableCell>
                  <TableCell>SR</TableCell>
                  <TableCell>SG</TableCell>
                  <TableCell>Volume</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Notif #</TableCell>
                  <TableCell>Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSalesOrders.map((salesOrder) =>
                  salesOrder.salesOrderProducts.map((salesOrderProduct) => (
                    <React.Fragment
                      key={`${salesOrder.salesOrderId}-${salesOrderProduct.salesOrderProductId}`}
                    >
                      <TableRow>
                        <TableCell>
                          <Typography>
                            {salesOrder.salesOrderDisplayId ||
                              salesOrder.otDisplayId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {formatTimestamp(
                              'id-ID',
                              salesOrderProduct.labelRequestedAt
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{salesOrder.brandId}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {salesOrderProduct.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {salesOrderProduct.packagingName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {salesOrderProduct.box ? 'X' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {salesOrderProduct.halal ? 'X' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {salesOrderProduct.wrap ? 'X' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {salesOrderProduct.seal ? 'X' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{salesOrderProduct.volume}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{salesOrderProduct.labelId}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {salesOrderProduct.notificationNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {salesOrderProduct.productQty}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={13}>
                          {salesOrderProduct.pictureUrl ? (
                            <Box display="flex" justifyContent="center">
                              <img
                                src={salesOrderProduct.pictureUrl}
                                alt="Packaging Set"
                                style={{
                                  maxWidth: 200,
                                  maxHeight: 200,
                                  objectFit: 'contain',
                                  border: '1px solid #ccc',
                                  borderRadius: 8,
                                }}
                              />
                            </Box>
                          ) : (
                            <Box display="flex" justifyContent="center">
                              <Typography>Tidak ada gambar</Typography>
                            </Box>
                          )}
                          {renderStjSummaryTable(salesOrder, salesOrderProduct)}
                        </TableCell>
                      </TableRow>

                      <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell colSpan={13}>
                          <Grid container alignItems="flex-start">
                            <Grid item xs={3}>
                              {salesOrderProduct.labelPrintedAt == null ? (
                                <Box ml={4} display="inline">
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                      setSelectedSalesOrderProduct(
                                        salesOrderProduct
                                      )
                                      setIsConfirmingLabelPrinted(true)
                                    }}
                                    disabled={isLoading}
                                  >
                                    Tandai Label Dicetak
                                  </Button>
                                </Box>
                              ) : (
                                <>
                                  <Typography display="inline">
                                    <Box fontWeight="bold" display="inline">
                                      Tanggal Label Dicetak:{' '}
                                    </Box>
                                    {formatTimestamp(
                                      'id-ID',
                                      salesOrderProduct.labelPrintedAt
                                    )}
                                  </Typography>
                                  <Typography>
                                    <Box fontWeight="bold" display="inline">
                                      Qty Label Yang Sudah Dicetak:{' '}
                                    </Box>
                                    {salesOrderProduct.labelAmount ?? '-'}
                                  </Typography>
                                </>
                              )}
                            </Grid>

                            <Grid item xs={3}>
                              {renderLabelReceivedSection(salesOrderProduct)}
                            </Grid>

                            <Grid item xs={3}>
                              {salesOrderProduct.labelingCompletedAt == null ? (
                                (() => {
                                  const reasons: string[] = []
                                  if (
                                    salesOrderProduct.labelPrintedAt == null
                                  ) {
                                    reasons.push('Label belum dicetak')
                                  }
                                  if (
                                    salesOrderProduct.labelReceivedAt == null
                                  ) {
                                    reasons.push(
                                      'Label belum diterima produksi'
                                    )
                                  }
                                  if (
                                    !salesOrder.stockCards.some(
                                      (sc) =>
                                        sc.isConfirmed &&
                                        sc.type ===
                                          STOCK_CARD_TYPE_HALF_COMPLETE
                                    )
                                  ) {
                                    reasons.push(
                                      'Belum ada Kartu Stok STJ yang dikonfirmasi untuk SO ini'
                                    )
                                  }
                                  const isDisabled =
                                    reasons.length > 0 || isLoading
                                  const tooltipTitle =
                                    reasons.length > 0
                                      ? `Tidak bisa tandai labelling selesai: ${reasons.join(
                                          '; '
                                        )}`
                                      : ''
                                  return (
                                    <>
                                      <Tooltip title={tooltipTitle} arrow>
                                        <span>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                              const so = salesOrders.find(
                                                (s) =>
                                                  s.salesOrderId ===
                                                  salesOrderProduct.salesOrderId
                                              )
                                              if (!so) return

                                              setLabelingContextSalesOrder(so)

                                              // Initialize data for eligible products in this SO
                                              const initialData: Record<
                                                number,
                                                {
                                                  labelUsedAmount: string
                                                  labelRejectedAmount: string
                                                  labelReturnedAmount: string
                                                  qtyKeJadi: string
                                                  batchData: Record<
                                                    string,
                                                    string
                                                  >
                                                }
                                              > = {}
                                              const initialSelected: number[] =
                                                []

                                              so.salesOrderProducts.forEach(
                                                (p) => {
                                                  // Eligibility check
                                                  if (
                                                    p.labelPrintedAt &&
                                                    p.labelReceivedAt &&
                                                    !p.labelingCompletedAt
                                                  ) {
                                                    const initialBatchData: Record<
                                                      string,
                                                      string
                                                    > = {}
                                                    const stjRows =
                                                      getStjSummaryRows(so, p)
                                                    stjRows.forEach(
                                                      (row, index) => {
                                                        const bid =
                                                          getStjSummaryRowKey(
                                                            row,
                                                            index
                                                          )
                                                        initialBatchData[bid] =
                                                          String(row.qty || 0)
                                                      }
                                                    )
                                                    const totalQty =
                                                      getStjSummaryTotalQty(
                                                        stjRows
                                                      )

                                                    initialData[
                                                      p.salesOrderProductId
                                                    ] = {
                                                      labelUsedAmount: '',
                                                      labelRejectedAmount: '',
                                                      labelReturnedAmount: '',
                                                      qtyKeJadi:
                                                        String(totalQty),
                                                      batchData:
                                                        initialBatchData,
                                                    }
                                                    initialSelected.push(
                                                      p.salesOrderProductId
                                                    )
                                                  }
                                                }
                                              )
                                              setLabelingData(initialData)
                                              setSelectedProductIds(
                                                initialSelected
                                              )
                                              setIsConfirmingLabelingComplete(
                                                true
                                              )
                                            }}
                                            disabled={isDisabled}
                                          >
                                            Tandai Labelling Selesai (ID: {salesOrderProduct.productId})
                                          </Button>
                                        </span>
                                      </Tooltip>
                                      {reasons.length > 0 && (
                                        <Box mt={1}>
                                          <Typography
                                            variant="caption"
                                            color="error"
                                            display="block"
                                          >
                                            Tidak bisa: {reasons.join('; ')}
                                          </Typography>
                                        </Box>
                                      )}
                                    </>
                                  )
                                })()
                              ) : (
                                <>
                                  <Typography display="inline">
                                    <Box fontWeight="bold" display="inline">
                                      Tanggal Label Selesai:{' '}
                                    </Box>
                                    {formatTimestamp(
                                      'id-ID',
                                      salesOrderProduct.labelingCompletedAt
                                    )}
                                  </Typography>
                                  <Typography>
                                    <Box fontWeight="bold" display="inline">
                                      Qty LABEL Ditempel:{' '}
                                    </Box>
                                    {salesOrderProduct.labelUsedAmount ?? '-'}
                                  </Typography>
                                  <Typography>
                                    <Box fontWeight="bold" display="inline">
                                      Qty LABEL Direject:{' '}
                                    </Box>
                                    {salesOrderProduct.labelRejectedAmount ??
                                      '-'}
                                  </Typography>
                                  <Typography>
                                    <Box fontWeight="bold" display="inline">
                                      Qty LABEL Dikembalikan:{' '}
                                    </Box>
                                    {salesOrderProduct.labelReturnedAmount ??
                                      '-'}
                                  </Typography>
                                </>
                              )}
                            </Grid>

                            <Grid item xs={3}>
                              {salesOrderProduct.labelReturnedAt == null ? (
                                (() => {
                                  const reasons: string[] = []
                                  if (
                                    salesOrderProduct.labelPrintedAt == null
                                  ) {
                                    reasons.push('Label belum dicetak')
                                  }
                                  if (
                                    salesOrderProduct.labelReceivedAt == null
                                  ) {
                                    reasons.push(
                                      'Label belum diterima produksi'
                                    )
                                  }
                                  if (
                                    salesOrderProduct.labelingCompletedAt ==
                                    null
                                  ) {
                                    reasons.push(
                                      'Labelling belum ditandai selesai'
                                    )
                                  }
                                  const tooltipTitle =
                                    reasons.length > 0
                                      ? `Tidak bisa tandai sisa label diterima: ${reasons.join(
                                          '; '
                                        )}`
                                      : ''
                                  return (
                                    <>
                                      <Tooltip title={tooltipTitle} arrow>
                                        <span>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                              setSelectedSalesOrderProduct(
                                                salesOrderProduct
                                              )
                                              setIsConfirmingLabelingReturned(
                                                true
                                              )
                                            }}
                                            disabled={
                                              reasons.length > 0 || isLoading
                                            }
                                          >
                                            Tandai Sisa Label Diterima
                                          </Button>
                                        </span>
                                      </Tooltip>
                                      {reasons.length > 0 && (
                                        <Box mt={1}>
                                          <Typography
                                            variant="caption"
                                            color="error"
                                            display="block"
                                          >
                                            Tidak bisa: {reasons.join('; ')}
                                          </Typography>
                                        </Box>
                                      )}
                                    </>
                                  )
                                })()
                              ) : (
                                <>
                                  <Typography>
                                    <Box fontWeight="bold" display="inline">
                                      Tanggal Sisa Label Diterima:
                                    </Box>{' '}
                                    {formatTimestamp(
                                      'id-ID',
                                      salesOrderProduct.labelReturnedAt
                                    )}
                                  </Typography>
                                  <Typography>
                                    <Box fontWeight="bold" display="inline">
                                      Qty LABEL Dikembalikan:
                                    </Box>{' '}
                                    {salesOrderProduct.labelConfirmedReturnedAmount ??
                                      '-'}
                                  </Typography>
                                  <Typography>
                                    <Box fontWeight="bold" display="inline">
                                      Qty LABEL Direject:{' '}
                                    </Box>
                                    {salesOrderProduct.labelRejectedReceivedAmount ??
                                      '-'}
                                  </Typography>
                                </>
                              )}
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        <ConfirmDialog
          title={`Tandai label dicetak?`}
          open={isConfirmingLabelPrinted}
          setOpen={setIsConfirmingLabelPrinted}
          onConfirm={markLabelPrinted}
        >
          <List>
            <ListItem>
              <TextField
                fullWidth
                label="Qty Dicetak"
                value={labelAmount}
                type="number"
                onChange={(e) => setLabelAmount(e.target.value)}
              />
            </ListItem>
          </List>
        </ConfirmDialog>
        <ConfirmDialog
          title={`Tandai STJ diterima?`}
          open={isConfirmingHalfCompleteReceived}
          setOpen={(open) => {
            setIsConfirmingHalfCompleteReceived(open)
            if (!open) {
              resetHalfCompleteContext()
            }
          }}
          onConfirm={markHalfCompleteReceived}
          fullWidth
          maxWidth="xl"
        >
          {halfCompleteContext.stockCards.length > 0 && (
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                style={{ color: '#000', fontWeight: 'bold' }}
              >
                STJ items:
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                style={{ overflowX: 'auto', marginBottom: 24 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f9f9f9' }}>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Qty STJ
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Kode Kemasan
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Kode Ruahan
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        No Bets
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Filling Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {flattenedStjItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatNumericDisplay(item.qty)}</TableCell>
                        <TableCell>{item.packagingId}</TableCell>
                        <TableCell>{item.premixId}</TableCell>
                        <TableCell>{item.batchNumber}</TableCell>
                        <TableCell>
                          {formatFillingAmountDisplay(
                            item.fillingAmount,
                            item.fillingUnit
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="h6"
                gutterBottom
                style={{ color: '#1976d2', fontWeight: 'bold' }}
              >
                Jadi items:
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                style={{ overflowX: 'auto' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f0f7ff' }}>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Qty (SO Requirement)
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Nama Produk
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold', minWidth: 150 }}>
                        Qty STJ Diterima (INPUT)
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Kode Kemasan
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Kode Ruahan
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        No Bets
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Label ID
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Filling Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(
                      halfCompleteContext.salesOrder?.salesOrderProducts || []
                    ).map((p) => (
                      <TableRow key={p.salesOrderProductId}>
                        <TableCell>
                          {formatNumericDisplay(p.productQty)}
                        </TableCell>
                        <TableCell>{p.productName || '-'}</TableCell>
                        <TableCell>
                          <TextField
                            value={
                              soProductReceivedAmounts[p.salesOrderProductId] ??
                              ''
                            }
                            onChange={(e) => {
                              const value = e.target.value
                              setSoProductReceivedAmounts((prev) => ({
                                ...prev,
                                [p.salesOrderProductId]: value,
                              }))
                            }}
                            placeholder="0"
                            type="number"
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 0 }}
                            style={{ minWidth: 120, backgroundColor: '#fff' }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>{p.packagingId || '-'}</TableCell>
                        <TableCell>{p.premixId || '-'}</TableCell>
                        <TableCell>{p.batchNumber || '-'}</TableCell>
                        <TableCell>{p.labelId || '-'}</TableCell>
                        <TableCell>
                          {formatFillingAmountDisplay(
                            Number(p.fillingAmount),
                            p.fillingUnit
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </ConfirmDialog>

        <ConfirmDialog
          title={`Tandai label diterima?`}
          open={isConfirmingLabelReceived}
          setOpen={setIsConfirmingLabelReceived}
          onConfirm={markLabellingReceived}
        >
          <List>
            <ListItem>
              <TextField
                fullWidth
                label="Qty LABEL Diterima Produksi"
                value={labelReceivedAmount}
                type="number"
                onChange={(e) => setLabelReceivedAmount(e.target.value)}
              />
            </ListItem>
          </List>
        </ConfirmDialog>
        <ConfirmDialog
          title={`Tandai selesai labeling`}
          open={isConfirmingLabelingComplete}
          setOpen={setIsConfirmingLabelingComplete}
          onConfirm={markLabelingCompleted}
          fullWidth
          maxWidth="xl"
          confirmDisabled={!isLabelingFormValid}
        >
          <Box mb={2}>
            <Typography variant="body2" color="error" gutterBottom>
              Total Qty LABEL Ditempel + Qty LABEL Direject + Qty LABEL
              Dikembalikan harus sama dengan Qty LABEL Diterima Produksi.
            </Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        (
                          labelingContextSalesOrder?.salesOrderProducts || []
                        ).filter((p) => labelingData[p.salesOrderProductId])
                          .length > 0 &&
                        selectedProductIds.length ===
                          (
                            labelingContextSalesOrder?.salesOrderProducts || []
                          ).filter((p) => labelingData[p.salesOrderProductId])
                            .length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProductIds(
                            (
                              labelingContextSalesOrder?.salesOrderProducts ||
                              []
                            )
                              .filter(
                                (p) => labelingData[p.salesOrderProductId]
                              )
                              .map((p) => p.salesOrderProductId)
                          )
                        } else {
                          setSelectedProductIds([])
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>ID Produk</TableCell>
                  <TableCell>Produk</TableCell>
                  <TableCell>Qty Dikeluarkan</TableCell>
                  <TableCell>Kode Kemasan</TableCell>
                  <TableCell>Kode Ruahan</TableCell>
                  <TableCell>No Bets</TableCell>
                  <TableCell>Qty LABEL Diterima Produksi</TableCell>
                  <TableCell style={{ minWidth: 120 }}>
                    Qty BARANG ke JADI
                  </TableCell>
                  <TableCell style={{ minWidth: 120 }}>
                    Qty LABEL Ditempel
                  </TableCell>
                  <TableCell style={{ minWidth: 120 }}>
                    Qty LABEL Direject
                  </TableCell>
                  <TableCell style={{ minWidth: 120 }}>
                    Qty LABEL Dikembalikan
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labelingContextSalesOrder?.salesOrderProducts
                  .filter((p) => labelingData[p.salesOrderProductId])
                  .map((product) => {
                    const data = labelingData[product.salesOrderProductId]
                    if (!data) return null
                    const stjSummaryTable = labelingContextSalesOrder
                      ? renderStjSummaryTable(
                          labelingContextSalesOrder,
                          product
                        )
                      : null
                    const stjRows = labelingContextSalesOrder
                      ? getStjSummaryRows(labelingContextSalesOrder, product)
                      : []
                    const qtyDikeluarkan = getStjSummaryTotalQty(stjRows)
                    return (
                      <React.Fragment key={product.salesOrderProductId}>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedProductIds.includes(
                                product.salesOrderProductId
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProductIds((prev) => [
                                    ...prev,
                                    product.salesOrderProductId,
                                  ])
                                } else {
                                  setSelectedProductIds((prev) =>
                                    prev.filter(
                                      (id) => id !== product.salesOrderProductId
                                    )
                                  )
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.productId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.productName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatNumericDisplay(qtyDikeluarkan)}
                          </TableCell>
                          <TableCell>
                            {product.packagingName ||
                              product.packagingId ||
                              '-'}
                          </TableCell>
                          <TableCell>{product.premixId || '-'}</TableCell>
                          <TableCell>{product.batchNumber || '-'}</TableCell>
                          <TableCell>
                            {formatNumericDisplay(product.labelReceivedAmount)}
                          </TableCell>
                          <TableCell>
                            {stjRows.length > 0 ? (
                              <Box>
                                {stjRows.map((row, index) => {
                                  const bid = getStjSummaryRowKey(row, index)
                                  return (
                                    <Box
                                      key={bid}
                                      mb={1}
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Typography
                                        variant="caption"
                                        style={{ minWidth: 80, marginRight: 8 }}
                                      >
                                        {row.batchNumber}:
                                      </Typography>
                                      <TextField
                                        disabled={
                                          !selectedProductIds.includes(
                                            product.salesOrderProductId
                                          )
                                        }
                                        value={data.batchData[bid] || ''}
                                        onChange={(e) =>
                                          handleBatchQtyChange(
                                            product.salesOrderProductId,
                                            bid,
                                            e.target.value
                                          )
                                        }
                                        type="number"
                                        variant="outlined"
                                        size="small"
                                        inputProps={{ min: 0 }}
                                        style={{ width: 100 }}
                                      />
                                    </Box>
                                  )
                                })}
                                <Box mt={1} pt={1} borderTop="1px dashed #ccc">
                                  <Typography variant="caption">
                                    <Box fontWeight="bold" display="inline">
                                      Total: {data.qtyKeJadi}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <TextField
                                disabled={
                                  !selectedProductIds.includes(
                                    product.salesOrderProductId
                                  )
                                }
                                value={data.qtyKeJadi}
                                onChange={(e) =>
                                  handleLabelingItemChange(
                                    product.salesOrderProductId,
                                    'qtyKeJadi',
                                    e.target.value
                                  )
                                }
                                type="number"
                                variant="outlined"
                                size="small"
                                inputProps={{ min: 0 }}
                                fullWidth
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              disabled={
                                !selectedProductIds.includes(
                                  product.salesOrderProductId
                                )
                              }
                              value={data.labelUsedAmount}
                              onChange={(e) =>
                                handleLabelingItemChange(
                                  product.salesOrderProductId,
                                  'labelUsedAmount',
                                  e.target.value
                                )
                              }
                              type="number"
                              variant="outlined"
                              size="small"
                              inputProps={{ min: 0 }}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              disabled={
                                !selectedProductIds.includes(
                                  product.salesOrderProductId
                                )
                              }
                              value={data.labelRejectedAmount}
                              onChange={(e) =>
                                handleLabelingItemChange(
                                  product.salesOrderProductId,
                                  'labelRejectedAmount',
                                  e.target.value
                                )
                              }
                              type="number"
                              variant="outlined"
                              size="small"
                              inputProps={{ min: 0 }}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              disabled={
                                !selectedProductIds.includes(
                                  product.salesOrderProductId
                                )
                              }
                              value={data.labelReturnedAmount}
                              onChange={(e) =>
                                handleLabelingItemChange(
                                  product.salesOrderProductId,
                                  'labelReturnedAmount',
                                  e.target.value
                                )
                              }
                              type="number"
                              variant="outlined"
                              size="small"
                              inputProps={{ min: 0 }}
                              fullWidth
                            />
                          </TableCell>
                        </TableRow>
                        {stjSummaryTable && (
                          <TableRow>
                            <TableCell colSpan={12}>
                              {stjSummaryTable}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          {labelingValidationError && (
            <Box mt={2}>
              <Typography color="error" variant="body2">
                {labelingValidationError}
              </Typography>
            </Box>
          )}
        </ConfirmDialog>
        <ConfirmDialog
          title={`Tandai label selesai diterima`}
          open={isConfirmingLabelingReturned}
          setOpen={setIsConfirmingLabelingReturned}
          onConfirm={markLabellingReturned}
        >
          <List>
            <ListItem>
              <TextField
                fullWidth
                label="Qty LABEL Dikembalikan"
                value={labelConfirmedReturnedAmount ?? ''}
                type="number"
                onChange={(e) =>
                  setLabelConfirmedReturnedAmount(e.target.value)
                }
              />
            </ListItem>
            <ListItem>
              <TextField
                fullWidth
                label="Qty LABEL Direject"
                value={labelRejectedReceivedAmount ?? ''}
                type="number"
                onChange={(e) => setLabelRejectedReceivedAmount(e.target.value)}
              />
            </ListItem>
          </List>
        </ConfirmDialog>

        <Snackbar
          open={isSuccessSnackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <MuiAlert severity="success" elevation={6} variant="filled">
            {alertMessage}
          </MuiAlert>
        </Snackbar>
        <Snackbar
          open={isFailureSnackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <MuiAlert severity="error" elevation={6} variant="filled">
            {alertMessage}
          </MuiAlert>
        </Snackbar>
      </Paper>
    </Box>
  )
}
